import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { pget, fmtStamp, STATUS, JOB_STATUS, brandTheme, communityLogoUrl } from "./portalApi";
import { homeLabel } from "@/lib/home";
import {
  MessageSquareText, PhoneCall, Wrench, Megaphone, CalendarDays, FileText,
  CalendarCheck, ChevronRight, Home, Sparkles, Folder, FolderOpen, Siren, CheckCircle2,
} from "lucide-react";

// The resident's own emergency report — read-only, disappears 72 h after it's resolved.
const EmergencyCard = ({ em, num, persona }) => {
  const open = em.status !== "resolved";
  return (
    <section data-testid="portal-emergency-card"
      className={`rounded-2xl border-2 p-5 ${open ? "border-red-300 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}>
      <div className="flex items-center gap-2">
        {open ? <Siren className="h-4 w-4 text-red-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-700" />}
        <h2 className={`font-heading font-bold text-sm uppercase tracking-wide ${open ? "text-red-800" : "text-emerald-900"}`}>
          {open ? "Your emergency report" : "Your emergency report — resolved"}
        </h2>
      </div>
      <p className="mt-2 text-sm font-semibold text-ink" data-testid="portal-emergency-issue">{em.issue}</p>
      <p className="mt-0.5 text-xs text-ink/60">Reported {em.reported_at}{em.ticket_ref ? ` · Request ${em.ticket_ref}` : ""}{em.photos > 0 ? ` · ${em.photos} photo${em.photos === 1 ? "" : "s"} shared with the team` : ""}</p>
      <p className="mt-2 text-sm text-ink" data-testid="portal-emergency-status">{em.status_line}</p>
      {open && num && (
        <a href={`sms:${num}`} data-testid="portal-emergency-text" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-red-600 px-4 py-2 text-xs font-bold text-white hover:bg-red-700">
          <MessageSquareText className="h-3.5 w-3.5" /> Text {persona} an update
        </a>
      )}
    </section>
  );
};

const Card = ({ icon: Icon, title, children, testid, to, accent }) => (
  <section data-testid={testid} className="bg-white rounded-2xl border border-sand-200 p-5 shadow-[0_1px_2px_rgba(25,22,20,0.04)]">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" strokeWidth={2} style={{ color: accent }} />
        <h2 className="font-heading font-bold text-ink text-sm uppercase tracking-wide">{title}</h2>
      </div>
      {to && <Link to={to} data-testid={`${testid}-viewall`} className="text-xs font-bold hover:opacity-70 transition-opacity" style={{ color: accent }}>View all →</Link>}
    </div>
    {children}
  </section>
);

const Empty = ({ children }) => <p className="text-sm text-ink/45 py-1">{children}</p>;

export default function PortalHome() {
  const { me } = useOutletContext();
  const [home, setHome] = useState(null);
  const [logoBroken, setLogoBroken] = useState(false);

  useEffect(() => {
    pget("/resident/home").then((r) => setHome(r.data)).catch(() => {});
  }, []);

  const theme = brandTheme(me.community?.brand_color);

  if (!home) {
    return <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: theme.accent, borderTopColor: "transparent" }} /></div>;
  }

  const first = (me.person?.name || "neighbor").split(" ")[0];
  const num = home.concierge_number || "";
  const persona = me.community?.persona_name || "the concierge";
  const logo = communityLogoUrl(me.community);
  const answersOnly = me.community?.mode === "answers_only";
  // answers_only + Field Services = tracking-only: residents can watch dispatched jobs.
  const trackingOnly = answersOnly && !!me.community?.field_services;
  const showRequests = !answersOnly || trackingOnly;
  const modules = me.community?.modules || {};

  // Things a resident can ask — a warm, useful "what can I do?" strip that also keeps the
  // page feeling full for answers-only communities that have few cards.
  const asks = [
    "Pool & amenity hours", "Guest parking rules", "Trash & recycling days",
    "How do I pay my dues?", "Report a maintenance issue", "Pet policy",
    ...(modules.events ? ["RSVP to an event"] : []),
    ...(modules.amenities ? ["Book the clubhouse"] : []),
    "Reach a real person",
  ];

  return (
    <div data-testid="portal-home" className="space-y-6">
      {/* HERO — community-branded */}
      <section
        data-testid="portal-hero"
        className="relative overflow-hidden rounded-3xl p-6 sm:p-8"
        style={{ backgroundColor: theme.heroBg, color: theme.heroText }}
      >
        <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full opacity-20 blur-2xl" style={{ background: theme.set ? theme.onAccent : "#e5342e" }} aria-hidden />
        <div className="relative">
          {logo && !logoBroken ? (
            <div className="inline-flex items-center bg-white rounded-xl px-3 py-2 mb-4 shadow-sm">
              <img src={logo} alt={me.community?.name} onError={() => setLogoBroken(true)} className="h-8 w-auto max-w-[160px] object-contain" data-testid="portal-hero-logo" />
            </div>
          ) : (
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: theme.heroSub }}>
              Your concierge · {persona}
            </div>
          )}
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold leading-tight" data-testid="portal-greeting">Hi, {first}!</h1>
          <p className="text-sm mt-1.5 flex items-center gap-1.5" style={{ color: theme.heroSub }}>
            <Home className="h-3.5 w-3.5" /> {me.community?.name}{homeLabel(me.household) ? ` · ${homeLabel(me.household)}` : ""}
          </p>
          {num && (
            <div className="flex gap-2.5 mt-5 flex-wrap">
              <a data-testid="portal-text-concierge" href={`sms:${num}`}
                className="rounded-full bg-white font-bold px-5 py-3 text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-sm"
                style={{ color: "#191614" }}>
                <MessageSquareText className="h-4 w-4" /> Text {persona}
              </a>
              <a data-testid="portal-call-concierge" href={`tel:${num}`}
                className="rounded-full bg-white/15 hover:bg-white/25 transition-colors font-bold px-5 py-3 text-sm flex items-center justify-center gap-2 border border-white/20"
                style={{ color: theme.heroText }}>
                <PhoneCall className="h-4 w-4" /> Call
              </a>
            </div>
          )}
        </div>
      </section>

      {home.emergency && <EmergencyCard em={home.emergency} num={num} persona={persona} />}

      {/* WHAT YOU CAN ASK — always present, keeps the page full & guides residents */}
      <section data-testid="portal-ask-help" className="rounded-2xl border p-5" style={{ backgroundColor: theme.accentSoft, borderColor: theme.accentBorder || theme.accentSoft }}>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4" strokeWidth={2} style={{ color: theme.accent }} />
          <h2 className="font-heading font-bold text-ink text-sm uppercase tracking-wide">What you can ask {persona}</h2>
        </div>
        <p className="text-sm text-ink/60 mb-3">No app, no login — just text or call anytime. For example:</p>
        <div className="flex flex-wrap gap-2">
          {asks.map((a) => (
            <span key={a} className="rounded-full bg-white border border-sand-200 px-3 py-1.5 text-xs font-semibold text-ink/75">{a}</span>
          ))}
        </div>
      </section>

      {/* DATA CARDS — single flowing grid so it never leaves an empty half */}
      <div className="grid gap-5 md:grid-cols-2 items-start">
        {showRequests && (
          <Card icon={Wrench} title="My requests" testid="portal-requests" to="/portal/requests" accent={theme.accent}>
            {home.requests.length === 0 && <Empty>{trackingOnly ? <>Nothing yet. Report an issue by texting {persona}.</> : <>Nothing open. Report an issue anytime by texting {persona}.</>}</Empty>}
            {home.requests.slice(0, 5).map((r) => {
              const pill = r.job_status ? (JOB_STATUS[r.job_status] || JOB_STATUS.reported) : (STATUS[r.status] || STATUS.open);
              return (
              <div key={r.id} className="flex items-center justify-between gap-3 py-2 border-b border-sand-100 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink truncate">{r.title || r.summary}</div>
                  <div className="text-xs text-ink/50">{r.ref} · {fmtStamp(r.created_at)}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${pill.cls}`}>{pill.label}</span>
              </div>
            );})}
          </Card>
        )}
        {modules.amenities && (
          <Card icon={CalendarCheck} title="My reservations" testid="portal-reservations" to="/portal/amenities" accent={theme.accent}>
            {home.reservations.length === 0 && <Empty>No upcoming bookings — grab a slot on the Amenities page.</Empty>}
            {home.reservations.map((r) => (
              <div key={r.id} className="py-2 border-b border-sand-100 last:border-0">
                <div className="text-sm font-semibold text-ink">{r.amenity_name}</div>
                <div className="text-xs text-ink/50">{r.date} · {r.start}–{r.end}</div>
              </div>
            ))}
          </Card>
        )}
        <Card icon={Megaphone} title="Announcements" testid="portal-announcements" accent={theme.accent}>
          {home.announcements.length === 0 && <Empty>No announcements right now. We&apos;ll post community news here.</Empty>}
          {home.announcements.map((a) => (
            <div key={a.id} className="py-2 border-b border-sand-100 last:border-0">
              <div className="text-sm font-semibold text-ink flex items-center gap-2">{a.urgent && <span className="rounded-full bg-danger-600 text-white px-1.5 py-px text-[10px] font-bold">URGENT</span>}{a.title}</div>
              <p className="text-xs text-ink/60 mt-0.5 line-clamp-2">{a.body}</p>
              <div className="text-[11px] text-ink/40 mt-0.5">{fmtStamp(a.created_at)}</div>
            </div>
          ))}
        </Card>
        {modules.events && (
          <Card icon={CalendarDays} title="Upcoming events" testid="portal-events" to="/portal/events" accent={theme.accent}>
            {home.events.length === 0 && <Empty>Nothing scheduled yet — check back soon.</Empty>}
            {home.events.map((e) => (
              <div key={e.id} className="py-2 border-b border-sand-100 last:border-0">
                <div className="text-sm font-semibold text-ink">{e.title}</div>
                <div className="text-xs text-ink/50">{e.date}{e.time ? ` · ${e.time}` : ""}</div>
              </div>
            ))}
          </Card>
        )}
        <Card icon={FileText} title="Community documents" testid="portal-documents" to="/portal/documents" accent={theme.accent}>
          {(home.document_folders || []).length === 0 && (home.documents || []).length === 0 && (
            <Empty>No documents shared yet — bylaws &amp; forms will appear here.</Empty>
          )}
          {/* Organized shelves: one row per folder the HOA maintains (with a count) */}
          {(home.document_folders || []).map((f) => (
            <Link key={f.id} to={`/portal/documents?folder=${f.id}`} data-testid={`portal-home-folder-${f.id}`}
              className="flex items-center gap-2.5 py-2 border-b border-sand-100 last:border-0 group">
              <span className="h-8 w-8 rounded-lg bg-sand-100 flex items-center justify-center shrink-0" style={{ color: theme.accent }}><Folder className="h-4 w-4" strokeWidth={1.75} /></span>
              <span className="text-sm font-semibold text-ink truncate flex-1 group-hover:opacity-70 transition-opacity">{f.name}</span>
              <span className="text-[11px] font-bold rounded-full bg-sand-100 text-ink-500 px-1.5 py-0.5 shrink-0">{f.doc_count}</span>
              <ChevronRight className="h-4 w-4 text-ink/30 shrink-0" />
            </Link>
          ))}
          {(home.document_folders || []).length > 0 && home.documents_unfiled > 0 && (
            <Link to="/portal/documents?folder=unfiled" data-testid="portal-home-folder-unfiled"
              className="flex items-center gap-2.5 py-2 border-b border-sand-100 last:border-0 group">
              <span className="h-8 w-8 rounded-lg bg-sand-100 flex items-center justify-center shrink-0" style={{ color: theme.accent }}><FolderOpen className="h-4 w-4" strokeWidth={1.75} /></span>
              <span className="text-sm font-semibold text-ink truncate flex-1 group-hover:opacity-70 transition-opacity">Other documents</span>
              <span className="text-[11px] font-bold rounded-full bg-sand-100 text-ink-500 px-1.5 py-0.5 shrink-0">{home.documents_unfiled}</span>
              <ChevronRight className="h-4 w-4 text-ink/30 shrink-0" />
            </Link>
          )}
          {/* Fallback for communities that haven't foldered their docs: the recent list */}
          {(home.document_folders || []).length === 0 && (home.documents || []).map((d) => (
            <Link key={d.id} to="/portal/documents" className="flex items-center justify-between py-2 border-b border-sand-100 last:border-0 group">
              <span className="text-sm font-semibold text-ink truncate group-hover:opacity-70 transition-opacity">{d.title}</span>
              <ChevronRight className="h-4 w-4 text-ink/30 shrink-0" />
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}
